import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import BodyStateBriefing from '../coach/BodyStateBriefing'
import NonWearableBriefing from './NonWearableBriefing'
import type { AppliedEvidenceRule } from '../../lib/dailyOverrideTypes'
import type { DailyNutritionSummary } from '../../lib/mealLogCore'
import type { WellnessPillars } from '../../lib/submitMorningWellness'

type MorningBriefingProps = {
  hasWearableData: boolean
  briefingText: string | null
  adjustments: Record<string, unknown>
  briefingDate: string
  isScheduledRest?: boolean
  showMorningBrief: boolean
  hasMorningText: boolean
  awaitingAck: boolean
  onAcknowledge: () => void
  onFinishBriefing: () => void
  dispatching?: boolean
  nutritionSummary: DailyNutritionSummary | null
  nutritionCoachNote?: string | null
  nutritionFirstDay?: boolean
  evidenceApplied: AppliedEvidenceRule[]
  pendingWellness: WellnessPillars | null
  embedded?: boolean
}

const MorningBriefing = ({
  hasWearableData,
  briefingText,
  adjustments,
  briefingDate,
  isScheduledRest = false,
  showMorningBrief,
  hasMorningText,
  awaitingAck,
  onAcknowledge,
  onFinishBriefing,
  dispatching = false,
  nutritionSummary,
  nutritionCoachNote,
  nutritionFirstDay = false,
  evidenceApplied,
  pendingWellness,
  embedded = false,
}: MorningBriefingProps) => {
  const { t } = useTranslation()

  const wearableReady = hasWearableData && showMorningBrief && hasMorningText

  const title = useMemo(
    () => (hasWearableData ? t('coach.briefing.title') : t('coach.briefing.nonWearableTitle')),
    [hasWearableData, t],
  )

  if (!hasWearableData) {
    return (
      <NonWearableBriefing
        title={title}
        nutritionSummary={nutritionSummary}
        nutritionCoachNote={nutritionCoachNote}
        nutritionFirstDay={nutritionFirstDay}
        evidenceApplied={evidenceApplied}
        pendingWellness={pendingWellness}
        awaitingFinish={awaitingAck}
        onFinish={onFinishBriefing}
        dispatching={dispatching}
        embedded={embedded}
      />
    )
  }

  if (!wearableReady) {
    return (
      <BodyStateBriefing
        briefingText={null}
        adjustments={adjustments}
        date={briefingDate}
        isScheduledRest={isScheduledRest}
        awaitingAck={awaitingAck}
        onAcknowledge={onAcknowledge}
        embedded={embedded}
      />
    )
  }

  return (
    <BodyStateBriefing
      briefingText={briefingText}
      adjustments={adjustments}
      date={briefingDate}
      isScheduledRest={isScheduledRest}
      awaitingAck={awaitingAck}
      onAcknowledge={onAcknowledge}
      embedded={embedded}
    />
  )
}

export default MorningBriefing
